import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
} from 'axios';

import { v4 as uuid } from './uuid';

// `||` rather than `??` so an empty-string build arg
// (`VITE_API_URL=`) still falls back to the relative path the nginx
// proxy serves on the same origin.
const baseURL = import.meta.env.VITE_API_URL || '/api/v1';

let accessToken: string | null = null;
let selectedTenantId: string | null = null;
let refreshFn: (() => Promise<string | null>) | null = null;
let onAuthLost: (() => void) | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function setSelectedTenantId(tenantId: string | null): void {
  selectedTenantId = tenantId;
}

export function getSelectedTenantId(): string | null {
  return selectedTenantId;
}

export function configureAuth(opts: {
  refresh: () => Promise<string | null>;
  onAuthLost: () => void;
}): void {
  refreshFn = opts.refresh;
  onAuthLost = opts.onAuthLost;
}

export interface ResponseEnvelope<T> {
  data: T;
  meta: { request_id?: string; version: string; page?: unknown };
}

export interface ErrorEnvelope {
  error: { code: string; message: string; details?: Record<string, unknown> };
  meta: { version: string };
}

export const api: AxiosInstance = axios.create({
  baseURL,
  withCredentials: true,
  timeout: 15_000,
});

api.interceptors.request.use((config) => {
  config.headers = config.headers ?? {};
  config.headers['X-Request-Id'] = uuid();
  if (accessToken && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  if (
    selectedTenantId &&
    !config.headers['X-Tenant-Id'] &&
    isTenantScopedRequest(config.url)
  ) {
    config.headers['X-Tenant-Id'] = selectedTenantId;
  }
  return config;
});

function isTenantScopedRequest(url?: string): boolean {
  if (!url) return false;
  const path = url.startsWith('http')
    ? new URL(url).pathname.replace(/^\/api\/v1/, '')
    : (url.split('?')[0] ?? '');
  return (
    path === '/auth/me' ||
    path === '/tenants/current' ||
    path.startsWith('/roles') ||
    path === '/permissions' ||
    path.startsWith('/memberships') ||
    path.startsWith('/files') ||
    path.startsWith('/api-keys') ||
    path.startsWith('/billing') ||
    path.startsWith('/notifications') ||
    path.startsWith('/qms')
  );
}

interface RetryableConfig extends AxiosRequestConfig {
  _retried?: boolean;
  /** Per-request opt-out from the 401 → refresh-and-retry interceptor.
   * Use for endpoints that authenticate themselves (e.g. ticket-bearing)
   * where retrying with a refreshed bearer would be meaningless or
   * would burn another single-use credential. */
  _skipAuthRetry?: boolean;
}

export type SubscriptionGateReason = 'required' | 'inactive';

export interface SubscriptionGateEvent {
  reason: SubscriptionGateReason;
  status: string | null;
}

export const SUBSCRIPTION_GATE_EVENT = 'saas-kit:subscription-gate';

function emitSubscriptionGate(error: AxiosError): void {
  if (error.response?.status !== 402) return;
  const body = error.response.data as ErrorEnvelope | undefined;
  const code = body?.error?.code;
  let reason: SubscriptionGateReason | null = null;
  if (code === 'billing.subscription_required') reason = 'required';
  else if (code === 'billing.subscription_inactive') reason = 'inactive';
  if (!reason) return;
  const status = (body?.error?.details?.status as string | undefined) ?? null;
  const detail: SubscriptionGateEvent = { reason, status };
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(SUBSCRIPTION_GATE_EVENT, { detail }));
  }
}

api.interceptors.response.use(
  (resp) => resp,
  async (error: AxiosError) => {
    const cfg = error.config as RetryableConfig | undefined;
    if (
      error.response?.status === 401 &&
      cfg &&
      !cfg._retried &&
      !cfg._skipAuthRetry &&
      refreshFn &&
      !cfg.url?.includes('/auth/refresh') &&
      !cfg.url?.includes('/auth/login')
    ) {
      cfg._retried = true;
      const next = await refreshFn();
      if (next) {
        cfg.headers = { ...(cfg.headers ?? {}), Authorization: `Bearer ${next}` };
        return api.request(cfg);
      }
      onAuthLost?.();
    }
    emitSubscriptionGate(error);
    return Promise.reject(error);
  },
);

export async function unwrap<T>(
  p: Promise<AxiosResponse<ResponseEnvelope<T>>>,
): Promise<T> {
  const resp = await p;
  return resp.data.data;
}

export function isAxiosError<T = unknown>(e: unknown): e is AxiosError<T> {
  return e instanceof AxiosError;
}

export interface SessionTokens {
  access_token: string;
  token_type: string;
  expires_at: string;
  tenant_id: string | null;
}

export async function login(email: string, password: string): Promise<SessionTokens> {
  const data = await unwrap<SessionTokens>(api.post('/auth/login', { email, password }));
  setAccessToken(data.access_token);
  return data;
}
